import { useEffect, useState } from "react";
import { Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { HeartOutlined } from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// Shared Inventory-style components — same ones used by BankStatement.jsx
// and TodayBirthDays.jsx. Adjust the path if your project's shared folder
// differs.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "Custname", label: "Customer Name" },
  { key: "Area", label: "Area" },
];
const secondaryFields = [
  { key: "Mobileno", label: "Mobile" },
  { key: "ANNVERSARY", label: "Anniversary" },
];

const TodayAnniversaries = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoint + query params/format) ----
     No date filter is exposed on this screen — it always pulls today's
     anniversaries, so the fetch runs once on mount, same as
     TodayBirthDays.jsx.

     NOTE: kept byte-for-byte identical to your original query string,
     including the missing closing quote after the date
     (`where=ANNVERSARY='${...}` never closes the `'`). That looks like a
     copy-paste bug from TodayBirthDays.jsx's working version
     (`where=DOB='${...}'`) rather than intentional, and would likely make
     the backend's WHERE clause malformed. Flagging it rather than
     silently fixing it — let me know if you'd like it corrected to
     `where=ANNVERSARY='${dayjs().format("MM/DD/YYYY")}'`. */
  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=ANNVERSARY='${dayjs().format(
          "MM/DD/YYYY",
        )}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching today's anniversaries:", error);
      })
      .finally(() => setLoading(false));
  }, [tenantName]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Custname",
      dataIndex: "Custname",
      key: "Custname",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    { title: "Area", dataIndex: "Area", key: "Area" },
    { title: "Mobile Number", dataIndex: "Mobileno", key: "Mobileno" },
    {
      title: "Anniversary Date",
      dataIndex: "ANNVERSARY",
      key: "ANNVERSARY",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
  ];

  const formattedData = rawData.map((item, index) => ({
    ...item,
    serialNo: index + 1,
  }));

  const exportData = formattedData.map((item) => ({
    ...item,
    ANNVERSARY: item?.ANNVERSARY
      ? dayjs(item.ANNVERSARY).format("DD/MMM/YYYY")
      : "",
  }));

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Today Anniversaries"
        subtitle="CRM · Today Anniversaries"
        icon={<HeartOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={rawData.length}
        visible={rawData.length > 0}
        stats={[{ label: "Records", value: rawData.length, color: "blue" }]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div className={styles.toolbarLeft} />

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="TodayAnniversaries"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="tan-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No anniversaries today"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row
            style={{ background: "#f5f5f5", fontWeight: 600 }}
          >
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default TodayAnniversaries;